// Procedural Web Audio synthesizer for rich sound effects & music cues without external asset files

class SoundManager {
  constructor() {
    this.ctx = null;
    this.isMuted = false;
    this.initOnFirstInteraction = this.initOnFirstInteraction.bind(this);
    window.addEventListener('pointerdown', this.initOnFirstInteraction, { once: true });
    window.addEventListener('keydown', this.initOnFirstInteraction, { once: true });
  }

  initAudio() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  initOnFirstInteraction() {
    this.initAudio();
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  // Play a simple synthesized sound using oscillators
  playTone(freq, type = 'sine', duration = 0.15, gainVal = 0.15, pitchDecay = 0) {
    if (this.isMuted) return;
    this.initAudio();
    if (!this.ctx) return;

    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, now);
      if (pitchDecay !== 0) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(20, freq + pitchDecay), now + duration);
      }

      gain.gain.setValueAtTime(gainVal, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now);
      osc.stop(now + duration);
    } catch (e) {
      console.warn('Audio tone play error', e);
    }
  }

  // UI Tap / Click
  playClick() {
    this.playTone(600, 'triangle', 0.05, 0.1, -200);
  }

  // Petting / Squish
  playPet() {
    if (this.isMuted) return;
    this.initAudio();
    if (!this.ctx) return;

    const notes = [440, 554.37, 659.25];
    notes.forEach((freq, idx) => {
      setTimeout(() => {
        this.playTone(freq, 'sine', 0.18, 0.12, 50);
      }, idx * 60);
    });
  }

  // Collect item / coin / food
  playCatch() {
    this.playTone(880, 'triangle', 0.12, 0.15, 400);
  }

  // Jump / bounce
  playJump() {
    this.playTone(320, 'sine', 0.14, 0.18, 280);
  }

  // Miss / hurt
  playHit() {
    this.playTone(180, 'sawtooth', 0.22, 0.18, -100);
  }

  // Match success / combo
  playCombo(level = 1) {
    const base = 400 + level * 70;
    this.playTone(base, 'triangle', 0.1, 0.15, 150);
  }

  // Bar gained (+3 life bars)
  playBarGain() {
    if (this.isMuted) return;
    const chords = [523.25, 659.25, 783.99, 1046.50]; // C E G C
    chords.forEach((freq, i) => {
      setTimeout(() => {
        this.playTone(freq, 'triangle', 0.2, 0.15, 20);
      }, i * 90);
    });
  }

  // Victory fanfare
  playVictory() {
    if (this.isMuted) return;
    const melody = [
      { f: 523.25, d: 0.12, t: 0 },
      { f: 659.25, d: 0.12, t: 120 },
      { f: 783.99, d: 0.12, t: 240 },
      { f: 1046.50, d: 0.35, t: 360 }
    ];
    melody.forEach(n => {
      setTimeout(() => {
        this.playTone(n.f, 'triangle', n.d, 0.2, 10);
      }, n.t);
    });
  }

  // Game over / Fail
  playGameOver() {
    if (this.isMuted) return;
    const notes = [
      { f: 440, d: 0.2, t: 0 },
      { f: 392, d: 0.2, t: 150 },
      { f: 349.23, d: 0.4, t: 300 }
    ];
    notes.forEach(n => {
      setTimeout(() => {
        this.playTone(n.f, 'sawtooth', n.d, 0.12, -40);
      }, n.t);
    });
  }

  // Mood soundscapes
  playMoodCue(mood) {
    if (this.isMuted) return;
    if (mood === 'happy') {
      [659.25, 783.99, 987.77, 1318.51].forEach((f, i) => {
        setTimeout(() => this.playTone(f, 'sine', 0.22, 0.12, 40), i * 80);
      });
    } else if (mood === 'serene') {
      [440, 554.37, 659.25].forEach((f, i) => {
        setTimeout(() => this.playTone(f, 'sine', 0.4, 0.08, 0), i * 140);
      });
    } else if (mood === 'sad') {
      [392, 349.23, 311.13].forEach((f, i) => {
        setTimeout(() => this.playTone(f, 'triangle', 0.35, 0.1, -30), i * 160);
      });
    } else if (mood === 'mad') {
      [220, 196, 174.61].forEach((f, i) => {
        setTimeout(() => this.playTone(f, 'sawtooth', 0.25, 0.15, -60), i * 100);
      });
    }
  }
}

export const sound = new SoundManager();
